import { useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import * as Switch from '@radix-ui/react-switch'
import { cn } from '@/lib/utils'

interface ModelRowProps {
  title: string
  summary: string          // one-line collapsed summary, e.g. "17 classes · 50%" or "off"
  enabled: boolean
  // Optional: omit for header-only rows that have no model-level on/off
  // (e.g. the audio model, where enable is per-class). No toggle is rendered
  // and the row is always expandable.
  onToggle?: (on: boolean) => void
  disabled?: boolean
  children?: ReactNode     // body (rendered when expanded)
  badge?: string           // e.g. "inactive"
}

export function ModelRow({ title, summary, enabled, onToggle, disabled, children, badge }: ModelRowProps) {
  const [open, setOpen] = useState(false)
  const expandable = onToggle ? enabled : true   // no toggle => always expandable
  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button type="button" onClick={() => setOpen((o) => !o)} disabled={!expandable}
                className="text-muted-foreground disabled:opacity-30">
          <ChevronRight className={cn('h-4 w-4 transition-transform', open && 'rotate-90')} />
        </button>
        {onToggle && (
          <Switch.Root
            checked={enabled}
            onCheckedChange={onToggle}
            disabled={disabled}
            className="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=unchecked]:bg-input disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Switch.Thumb className="pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
          </Switch.Root>
        )}
        <span className="text-sm font-medium flex-1">{title}</span>
        {badge && <span className="text-xs text-muted-foreground">{badge}</span>}
        <span className="text-xs text-muted-foreground tabular-nums">{summary}</span>
      </div>
      {open && expandable && <div className="border-t border-border px-3 py-3 space-y-3">{children}</div>}
    </div>
  )
}
